/**
 * The row's menu: what it offers, and where it opens. The decisions, with no
 * browser in them — `fileDrag.ts`'s discipline for the other act on a row.
 *
 * A row in the explorations sidebar carries ONE control besides its own press,
 * a ⋮ that opens this menu (D100). The acts that used to ride the row as four
 * glyphs — pin, fork, share, forget — are its items now, in the groups the
 * reference the owner pointed at draws: the door first, then the acts on the
 * exploration itself, then where it is filed, then the one act nobody can
 * undo, last and in the colour that says so. The component moves the pixels;
 * this says what the pixels mean, and a test can hold the order and the
 * wording still.
 *
 * Imports the sidebar's own vocabulary for a group's name and for a ring of
 * choices, so a target is called the same thing here that the keyboard lift
 * calls it, and the arrow keys wrap here the way they wrap there.
 */

import { projectSaid, stepAt } from './fileDrag'

/** The acts a menu item can fire. `open` is the row's own press restated, for
 *  whoever reached the menu first; it is left out of the open row's menu. */
export type RowAct = 'open' | 'pin' | 'unpin' | 'rename' | 'fork' | 'share' | 'forget'

/**
 * One place a row could be moved to.
 *
 * The group it is in NOW is offered and marked rather than left out: choosing
 * it is "left where it was", which `fileRow` already says, and a list with a
 * hole in it is a list the reader has to re-read to find the hole.
 */
export interface MoveTarget {
  project: string | null
  label: string
  current: boolean
}

/** One line of a menu, over the acts that menu can fire. A row's menu and a
 *  project's are the same component and the same keys (D368); only the acts
 *  differ, so the act is the parameter and the rest is shared. */
export type MenuItem<A extends string> =
  | { kind: 'act'; act: A; label: string; hint?: string; tone?: 'bad' }
  | { kind: 'move'; label: string; hint: string; targets: MoveTarget[] }
  | { kind: 'sep' }

export type RowMenuItem = MenuItem<RowAct>

/**
 * The letters a focused row answers with, printed beside the items they fire.
 *
 * Checked against `App.tsx`'s map rather than assumed free, as `M` was: `f`
 * frames the graph, `s` skips a coach cue while the card is up, and `p` runs
 * the three lenses on the selection. Each is shadowed only while a ROW has
 * focus, which is what the row already does for `M` — a key the row consumes
 * is stopped before the ladder sees it — and a reader with a row under their
 * fingers means the row. `M` is the lift (`fileDrag.ts`), the keyboard's own
 * way to the same door the submenu opens onto. `R` opens the row's name field;
 * on the canvas `r` is the Record form, which is about a node and never a row,
 * and a focused row is a button whose letters are its own (D161) anyway.
 */
export const ROW_KEYS = { pin: 'P', rename: 'R', fork: 'F', share: 'S', move: 'M' } as const

/**
 * What the menu lists, top to bottom.
 *
 * The OPEN exploration's menu has no Open, because the row's own press already
 * does nothing for it. Move to project appears only when there is somewhere
 * else to go — with one group on the device the submenu would hold one row,
 * already marked current, which is a door onto a wall.
 *
 * RECENT IS ALWAYS A TARGET (D368), drawn or not. The groups on screen are the
 * targets everywhere else, and Recent draws only over unfiled rows — so once
 * every row was filed, a filed row's menu lost its way back out, and with one
 * project on the device lost Move to project altogether. The submenu names what
 * it offers, so a target there is one somebody can see.
 */
export function rowMenuItems(o: {
  isOpen: boolean
  pinned: boolean
  candidates: readonly (string | null)[]
  from: string | null
}): RowMenuItem[] {
  const items: RowMenuItem[] = []
  if (!o.isOpen) items.push({ kind: 'act', act: 'open', label: 'Open' }, { kind: 'sep' })
  items.push(
    {
      kind: 'act',
      act: o.pinned ? 'unpin' : 'pin',
      label: o.pinned ? 'Unpin' : 'Pin',
      hint: ROW_KEYS.pin,
    },
    // Beside the fork, because a fork is when a name is wanted: two rows that
    // say the same thing until one of them is told apart.
    { kind: 'act', act: 'rename', label: 'Rename', hint: ROW_KEYS.rename },
    { kind: 'act', act: 'fork', label: 'Fork', hint: ROW_KEYS.fork },
    { kind: 'act', act: 'share', label: 'Share or export', hint: ROW_KEYS.share },
  )
  const places = o.candidates.includes(null) ? o.candidates : [...o.candidates, null]
  const targets = places.map((project) => ({
    project,
    label: projectSaid(project),
    current: project === o.from,
  }))
  if (targets.some((t) => !t.current)) {
    items.push(
      { kind: 'sep' },
      { kind: 'move', label: 'Move to project', hint: ROW_KEYS.move, targets },
    )
  }
  items.push({ kind: 'sep' }, { kind: 'act', act: 'forget', label: 'Forget', tone: 'bad' })
  return items
}

/** The acts a project's heading offers (D368). */
export type ProjectAct = 'new' | 'fold' | 'unfold' | 'rename' | 'remove'

/** The letters a project's menu answers while it is up. Printed beside the
 *  items, and read by the menu only: a focused heading is a button, and its
 *  letters stay its own, as a row's do. */
export const PROJECT_KEYS = { new: 'N', rename: 'R' } as const

/**
 * What a project's heading lists, on a right-click or ⇧F10 (D368): the acts on
 * the PROJECT, never on the explorations in it — those are each row's own.
 *
 * Making first, because it is the common act and the one the heading's + is
 * for. Then the fold, only with members, for D101's reason: a folder that shut
 * on nothing would be a control that lies. Remove only while the project is
 * empty, last and in the colour that says so, for the reason the ✕ is only
 * there then — on a project with members it would be a question about the
 * explorations rather than about the label.
 */
export function projectMenuItems(o: { members: number; folded: boolean }): MenuItem<ProjectAct>[] {
  const items: MenuItem<ProjectAct>[] = [
    { kind: 'act', act: 'new', label: 'New exploration here', hint: PROJECT_KEYS.new },
    { kind: 'sep' },
  ]
  if (o.members > 0) {
    items.push(
      o.folded
        ? { kind: 'act', act: 'unfold', label: 'Unfold' }
        : { kind: 'act', act: 'fold', label: 'Fold' },
    )
  }
  items.push({ kind: 'act', act: 'rename', label: 'Rename', hint: PROJECT_KEYS.rename })
  if (o.members === 0) {
    items.push(
      { kind: 'sep' },
      { kind: 'act', act: 'remove', label: 'Remove project', tone: 'bad' },
    )
  }
  return items
}

/** The indices a highlight can rest on — everything but a separator. */
export function pressable(items: readonly MenuItem<string>[]): number[] {
  return items.flatMap((it, i) => (it.kind === 'sep' ? [] : [i]))
}

/** One step through the pressable items, wrapping at both ends — the same ring
 *  the lift walks. `at` and the answer are indices into `items`, not into the
 *  pressable list, so the caller never has to translate. */
export function stepPressable(items: readonly MenuItem<string>[], at: number, by: number): number {
  const ring = pressable(items)
  if (ring.length === 0) return 0
  const i = ring.indexOf(at)
  return ring[stepAt(i < 0 ? 0 : i, i < 0 ? 0 : by, ring.length)]!
}

/** Where a submenu's highlight starts: on the group the row is in, so the first
 *  ↑ or ↓ moves one place from where the reader is. Or the top. */
export function subStart(targets: readonly MoveTarget[]): number {
  const i = targets.findIndex((t) => t.current)
  return i < 0 ? 0 : i
}

/* ── placement ─────────────────────────────────────────────────────────── */

export interface Rect {
  left: number
  top: number
  right: number
  bottom: number
}
export interface Size {
  width: number
  height: number
}

/** The menu's own padding, which a submenu subtracts so its first row sits
 *  level with the item that opened it. The component reads the same number. */
export const MENU_PAD = 6
/** Daylight between the button and its menu, and between a menu and its
 *  submenu. */
export const MENU_GAP = 4
/** How close to the window's edge a menu may come. */
export const MENU_MARGIN = 8

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi))

/**
 * Where the menu opens against its ⋮: hanging below it with their right edges
 * flush, the way the reference hangs, and ABOVE when the room below has run
 * out — a menu opened on the last row of a long list must not run off the
 * bottom of the window. Clamped inside the window on every side after that,
 * so a window shorter than the menu still shows its top.
 */
export function placeMenu(
  anchor: Rect,
  size: Size,
  viewport: Size,
): { left: number; top: number; above: boolean } {
  const below = anchor.bottom + MENU_GAP
  const fitsBelow = below + size.height <= viewport.height - MENU_MARGIN
  const above = !fitsBelow && anchor.top - MENU_GAP - size.height >= MENU_MARGIN
  const top = clamp(
    above ? anchor.top - MENU_GAP - size.height : below,
    MENU_MARGIN,
    viewport.height - MENU_MARGIN - size.height,
  )
  const left = clamp(
    anchor.right - size.width,
    MENU_MARGIN,
    viewport.width - MENU_MARGIN - size.width,
  )
  return { left, top, above }
}

/**
 * Where the menu opens against the POINTER, for a right-click (D368): its
 * top-left corner on the point, as every menu a right-click raises opens —
 * the ⋮ is where a press aims, and the pointer is where a right-click aims.
 * Flipped above the point when the room below has run out and to its left
 * when the room to the right has, then clamped inside the window like
 * `placeMenu`. The ⋮ is still the menu's anchor in every other sense: focus
 * goes back to it when the menu shuts.
 */
export function placeAtPoint(
  at: { x: number; y: number },
  size: Size,
  viewport: Size,
): { left: number; top: number } {
  const below = at.y + size.height <= viewport.height - MENU_MARGIN
  const right = at.x + size.width <= viewport.width - MENU_MARGIN
  const top = clamp(
    below ? at.y : at.y - size.height,
    MENU_MARGIN,
    viewport.height - MENU_MARGIN - size.height,
  )
  const left = clamp(
    right ? at.x : at.x - size.width,
    MENU_MARGIN,
    viewport.width - MENU_MARGIN - size.width,
  )
  return { left, top }
}

/**
 * Where a submenu opens against the item that owns it: beside the menu on the
 * right, its first row level with the item, and on the LEFT when the right
 * has no room. The sidebar hugs the window's left edge, so the right is the
 * common case and the left the phone overlay's; when neither side fits it
 * stays on the right, clamped, and overlaps — the fallback is a menu that can
 * still be read rather than one that cannot.
 */
export function placeSubmenu(
  item: Rect,
  menu: Rect,
  size: Size,
  viewport: Size,
): { left: number; top: number; side: 'right' | 'left' } {
  const rightAt = menu.right + MENU_GAP
  const leftAt = menu.left - MENU_GAP - size.width
  const fitsRight = rightAt + size.width <= viewport.width - MENU_MARGIN
  const side = fitsRight || leftAt < MENU_MARGIN ? 'right' : 'left'
  const left = clamp(
    side === 'right' ? rightAt : leftAt,
    MENU_MARGIN,
    viewport.width - MENU_MARGIN - size.width,
  )
  const top = clamp(item.top - MENU_PAD, MENU_MARGIN, viewport.height - MENU_MARGIN - size.height)
  return { left, top, side }
}

/**
 * Whether a right-click on a row opens its ⋮ menu instead of the browser's (D335).
 *
 * Right-clicking an item for its actions is what every file and chat sidebar a
 * reader comes from teaches, and here it makes the whole row the target for a
 * menu whose button is a 30px glyph that is invisible until hover. The keyboard
 * already had it: the Menu key and ⇧F10 press the ⋮ (`rowKeys`).
 *
 * Never on touch. A long press there is the row's lift (`beginPress`), and
 * Android's Chrome fires `contextmenu` on that same long press. `pointerType`
 * is read off the event where the browser sets it; where it does not (Safari's
 * `contextmenu` is a plain mouse event), whether the primary pointer is fine.
 */
export function rightClickOpensMenu(
  pointerType: string | undefined,
  finePointer: boolean,
  /** Whether a long press here already means something. A row's does — it is
   *  the lift — and a project's heading's does not, so a heading's long press
   *  opens its menu where the browser fires one for it (D368). */
  longPressIsLift = true,
): boolean {
  if (pointerType === 'touch') return !longPressIsLift
  if (pointerType === 'mouse' || pointerType === 'pen') return true
  return finePointer || !longPressIsLift
}

/**
 * Where ↑, ↓, Home and End move focus among the sidebar's rows, from the row at
 * `at` of `total`, or null for any other key (D335). Each row is two tab stops,
 * its press and its ⋮, so the fifteenth exploration was some thirty Tabs away;
 * the node list and the step rail both answer arrows, and so do the file and
 * chat sidebars a reader brings habits from. The ends are walls, as the rail's
 * are (`railKey`): a list has a first row and a last one.
 */
export function rowStep(key: string, at: number, total: number): number | null {
  if (total < 1) return null
  const last = total - 1
  switch (key) {
    case 'ArrowDown':
      return Math.min(last, at + 1)
    case 'ArrowUp':
      return Math.max(0, at - 1)
    case 'Home':
      return 0
    case 'End':
      return last
  }
  return null
}
